/**
 * Support widget — the wire contract of `GET /api/public/v3/support/`.
 *
 * This file is the single TypeScript counterpart to `SupportStateSerializerV3`
 * (douleutaras `main/apis/public/v3/serializers.py`). Before it existed, the same shape
 * was handwritten twice — once in the Django loader, once in the fe-react slice — and
 * the two copies disagreed about which fields were optional. Nothing typechecks this
 * against the serializer across repos, so keep the two in lockstep by hand and lean on
 * the API-shape tests.
 *
 * The payload is plain DRF, so it is snake_case. It stays snake_case in this file
 * precisely because this is the wire, not the app; `normalize.ts` is the one place it
 * becomes camelCase.
 *
 * Every field is required, matching the serializer — each sub-serializer uses
 * `source='*'` over required fields, so the backend always sends the full object.
 * `valid_until` is the only nullable one. Readers should still be defensive: a payload
 * that reached the client is not necessarily one the backend sent (see `validate.ts`).
 */
import { CHAT_PROVIDER_SALESFORCE, CHAT_PROVIDER_SALESFORCE_NEW } from "./constants"

/**
 * Availability, resolved by the backend from the working hours and the Salesforce
 * flags. `hidden` means: render no support entry point at all.
 *
 * Mirrors `SUPPORT_STATE_*` in douleutaras `main/const.py`.
 */
export type SupportAvailability = "available" | "offline" | "hidden"

/** Every value `SupportAvailability` admits, for runtime checks. Order is not meaningful. */
export const SUPPORT_AVAILABILITIES: SupportAvailability[] = ["available", "offline", "hidden"]

/** Which chat implementation to bootstrap. Mirrors `CHAT_PROVIDER_*` in `main/const.py`. */
export type SupportChatProvider = typeof CHAT_PROVIDER_SALESFORCE | typeof CHAT_PROVIDER_SALESFORCE_NEW | string

export interface SupportApiChatChannel {
  enabled: boolean
  /** Selects the bootstrap (new vs. legacy Salesforce) — NOT a second availability switch. */
  provider: SupportChatProvider
}

export interface SupportApiCallChannel {
  enabled: boolean
  phone: string
}

export interface SupportApiContactFormChannel {
  enabled: boolean
}

/**
 * The three channels, each resolved from the same support state. A channel that is not
 * `enabled` is not rendered — there is no disabled state (Repo A plan §4).
 */
export interface SupportApiChannels {
  chat: SupportApiChatChannel
  call: SupportApiCallChannel
  contact_form: SupportApiContactFormChannel
}

/** Any one of the three, for code that only needs `enabled`. */
export type SupportApiChannel = SupportApiChatChannel | SupportApiCallChannel | SupportApiContactFormChannel

/**
 * Working hours as data rather than prose. The response is served from a shared cache
 * that cannot vary by language, so composing a sentence from these values is the
 * frontend's job — see `hours.ts`.
 */
export interface SupportApiSchedule {
  timezone: string // 'Europe/Athens'
  weekdays: number[] // ISO weekdays, 1 = Monday … 7 = Sunday
  opens_at: string // 'HH:MM'
  closes_at: string // 'HH:MM'
}

/** Admin-tunable widget behavior, delivered here rather than baked into CDN-cached HTML. */
export interface SupportApiWidget {
  enabled: boolean
  timer_seconds: number
  show_outside_working_hours: boolean
}

/**
 * The raw Salesforce admin flags `state` was resolved from. Published for surfaces that
 * genuinely need an individual switch; recombining them into a second verdict would put
 * the precedence rules in two places. Branch on `state`.
 */
export interface SupportApiFlags {
  use_salesforce_chat: boolean
  use_salesforce_chat_new: boolean
  use_salesforce_contact_us: boolean
}

export interface SupportApiResponse {
  state: SupportAvailability
  is_open: boolean
  /** When the answer stops being true (ISO 8601), or `null` when it does not expire. */
  valid_until: string | null
  channels: SupportApiChannels
  schedule: SupportApiSchedule
  widget: SupportApiWidget
  flags: SupportApiFlags
}
